import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  IconButton,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { ProductType } from '../App';
import StepHeading from './StepHeading';
import WizardFooter from './WizardFooter';

interface DimensionsProps {
  selectedProducts: ProductType[];
  onBack: () => void;
  onNext: (values: any) => void;
  onQuantitiesChange?: (quantities: Record<string, number>) => void;
}

const getProductName = (productId: ProductType): string => {
  const names: Record<ProductType, string> = {
    spannrahmen_fenster: 'Spannrahmen',
    drehfenster: 'Drehfenster',
    pendelfenster: 'Pendelfenster',
    rollo_fenster: 'Fenster Rollo',
    rollo_dachfenster: 'Dachfenster Rollo',
    drehtuer: 'Drehtür',
    pendeltuer: 'Pendeltür',
    plisseetuer: 'Plisseetür',
    rollo_tuer: 'Tür-Rollo',
    schiebetuer: 'Schiebetür',
    lichtschacht: 'Lichtschacht',
  };
  return names[productId] || productId;
};

// Validierung des deutschen Dezimalformats (z. B. 45,5)
const isValidGermanDecimal = (value: string) => {
  return /^\d+(?:,\d)?$/.test(value);
};

// Umrechnung von Zentimeter in Millimeter
const cmToMm = (cm: string): number => {
  const numValue = parseFloat(cm.replace(',', '.'));
  return Math.round(numValue * 10);
};

const createDimensionSchema = () =>
  yup.string()
    .required('Erforderlich')
    .test('is-german-decimal', 'Bitte geben Sie eine gültige Zahl ein (z.B. 45,5)', isValidGermanDecimal)
    .test('positive', 'Bitte geben Sie einen positiven Wert ein', value => {
      if (!value) return false;
      return cmToMm(value) > 0;
    });

const MIN_QUANTITY = 1;
const MAX_QUANTITY = 50;

const Dimensions: React.FC<DimensionsProps> = ({ selectedProducts, onBack, onNext, onQuantitiesChange }) => {
  const initialValues: any = {};
  const validationSchema: Record<string, any> = {};

  selectedProducts.forEach(productId => {
    initialValues[`${productId}_quantity`] = 1;
    validationSchema[`${productId}_quantity`] = yup
      .number()
      .min(MIN_QUANTITY, 'Mindestens 1')
      .max(MAX_QUANTITY, 'Maximal 50')
      .required('Erforderlich');

    for (let i = 1; i <= MAX_QUANTITY; i++) {
      initialValues[`${productId}_width_${i}`] = '';
      initialValues[`${productId}_height_${i}`] = '';

      validationSchema[`${productId}_width_${i}`] = yup.mixed().when(`${productId}_quantity`, {
        is: (val: any) => i <= val,
        then: () => createDimensionSchema(),
        otherwise: () => yup.mixed().notRequired(),
      });

      validationSchema[`${productId}_height_${i}`] = yup.mixed().when(`${productId}_quantity`, {
        is: (val: any) => i <= val,
        then: () => createDimensionSchema(),
        otherwise: () => yup.mixed().notRequired(),
      });
    }
  });

  const formik = useFormik({
    initialValues,
    validationSchema: yup.object(validationSchema),
    onSubmit: values => {
      // Konvertiere alle Zentimeter-Werte in Millimeter vor dem Submit
      const convertedValues: any = { ...values };
      selectedProducts.forEach(productId => {
        const quantity = values[`${productId}_quantity`];
        for (let i = 1; i <= quantity; i++) {
          const widthKey = `${productId}_width_${i}`;
          const heightKey = `${productId}_height_${i}`;
          if (values[widthKey]) {
            convertedValues[widthKey] = cmToMm(values[widthKey]);
          }
          if (values[heightKey]) {
            convertedValues[heightKey] = cmToMm(values[heightKey]);
          }
        }
      });
      onNext(convertedValues);
    },
  });

  const notifyQuantities = (values: any) => {
    if (!onQuantitiesChange) return;
    const quantities: Record<string, number> = {};
    selectedProducts.forEach(productId => {
      quantities[productId] = Number(values[`${productId}_quantity`]) || 1;
    });
    onQuantitiesChange(quantities);
  };

  useEffect(() => {
    notifyQuantities(formik.values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setQuantity = (productId: ProductType, next: number) => {
    const clamped = Math.min(MAX_QUANTITY, Math.max(MIN_QUANTITY, next));
    formik.setFieldValue(`${productId}_quantity`, clamped);
    notifyQuantities({ ...formik.values, [`${productId}_quantity`]: clamped });
  };

  const copyDimensions = (productId: string, sourceIndex: number) => {
    const width = formik.values[`${productId}_width_${sourceIndex}`];
    const height = formik.values[`${productId}_height_${sourceIndex}`];
    const quantity = formik.values[`${productId}_quantity`];

    for (let i = sourceIndex + 1; i <= quantity; i++) {
      formik.setFieldValue(`${productId}_width_${i}`, width);
      formik.setFieldValue(`${productId}_height_${i}`, height);
    }
  };

  const stepperButtonSx = {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: '8px',
    width: 34,
    height: 34,
    color: 'text.primary',
    bgcolor: '#fff',
    '&:hover': { bgcolor: 'grey.50' },
  } as const;

  const dimensionField = (productId: ProductType, index: number, dimension: 'width' | 'height') => {
    const fieldName = `${productId}_${dimension}_${index}`;
    return (
      <TextField
        fullWidth
        id={fieldName}
        name={fieldName}
        value={formik.values[fieldName]}
        onChange={formik.handleChange}
        error={formik.touched[fieldName] && Boolean(formik.errors[fieldName])}
        helperText={formik.touched[fieldName] && (formik.errors[fieldName] as string)}
        InputProps={{
          endAdornment: <InputAdornment position="end">cm</InputAdornment>,
        }}
        variant="outlined"
        placeholder={dimension === 'width' ? 'Breite' : 'Höhe'}
        size="medium"
      />
    );
  };

  return (
    <Box>
      <StepHeading step={3} title="Anzahl & Maße" />

      <Box
        sx={{
          mb: 3,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.25,
          bgcolor: '#E8F1FB',
          p: 2,
          borderRadius: '12px',
        }}
      >
        <InfoOutlinedIcon sx={{ color: 'primary.main', mt: 0.2, fontSize: 20 }} />
        <Typography variant="body2" sx={{ color: 'text.primary' }}>
          Geben Sie die <strong>ungefähren</strong> Maße in Zentimetern an (z. B. 45,5 cm). Die
          Angaben müssen nicht exakt sein — bei Auftrag erfolgt ein kostenloser Vor-Ort-Termin mit
          exaktem Aufmaß.
        </Typography>
      </Box>

      <form onSubmit={formik.handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {selectedProducts.map(productId => {
            const quantity = Number(formik.values[`${productId}_quantity`]) || 1;
            return (
              <Box
                key={productId}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '12px',
                  bgcolor: '#fff',
                  p: { xs: 2, sm: 3 },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                    {getProductName(productId)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Anzahl
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label="Anzahl verringern"
                      onClick={() => setQuantity(productId, quantity - 1)}
                      disabled={quantity <= MIN_QUANTITY}
                      sx={stepperButtonSx}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                    <Typography sx={{ fontWeight: 700, minWidth: 24, textAlign: 'center' }}>
                      {quantity}
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label="Anzahl erhöhen"
                      onClick={() => setQuantity(productId, quantity + 1)}
                      disabled={quantity >= MAX_QUANTITY}
                      sx={stepperButtonSx}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {Array.from({ length: quantity }).map((_, index) => (
                    <Grid container spacing={1.5} key={index} alignItems="center">
                      <Grid item xs={12} sm={2.5}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                            {quantity === 1 ? 'Maße' : `${getProductName(productId)} ${index + 1}`}
                          </Typography>
                          {index === 0 && quantity > 1 && (
                            <Tooltip title="Maße für alle übernehmen">
                              <IconButton
                                size="small"
                                onClick={() => copyDimensions(productId, 1)}
                                sx={{ ml: 0.5 }}
                              >
                                <ContentCopyIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={4.75}>
                        {dimensionField(productId, index + 1, 'width')}
                      </Grid>
                      <Grid item xs={6} sm={4.75}>
                        {dimensionField(productId, index + 1, 'height')}
                      </Grid>
                    </Grid>
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>

        <WizardFooter onBack={onBack} nextLabel="Weiter →" nextType="submit" />
      </form>
    </Box>
  );
};

export default Dimensions;
